// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, supabaseQueries } from '../utils/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Separate async operations object
  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        const { data: profile, error: profileError } = await supabaseQueries?.getUserProfile(userId);
        if (!profileError && profile) {
          setUserProfile(profile);
          setTenant(profile?.tenant);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setProfileLoading(false);
      }
    },
    
    clear() {
      setUserProfile(null);
      setTenant(null);
      setProfileLoading(false);
    }
  };

  // Protected auth handlers
  const authStateHandlers = {
    // CRITICAL: This MUST remain synchronous
    onChange: (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id); // Fire-and-forget
      } else {
        profileOperations?.clear();
      }
    }
  };

  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    );

    return () => subscription?.unsubscribe();
  }, []);

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      return { user: data?.user, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { user: null, error: error?.message };
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });
      
      if (error) throw error;
      return { user: data?.user, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { user: null, error: error?.message };
    }
  };

  const signInWithSSO = async (provider, domain) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider,
        options: {
          queryParams: {
            domain: domain,
            prompt: 'select_account'
          },
          redirectTo: `${window.location?.origin}/auth/callback`
        }
      });
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('SSO sign in error:', error);
      return { error: error?.message };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      
      setUser(null);
      profileOperations?.clear();
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error: error?.message };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`
      });
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Password reset error:', error);
      return { error: error?.message };
    }
  };

  // Utility functions
  const hasRole = (role) => {
    return userProfile?.role === role;
  };

  const hasAnyRole = (roles) => {
    return roles?.includes(userProfile?.role);
  };

  const isTenantAdmin = () => {
    return hasAnyRole(['super_admin', 'tenant_admin']);
  };

  const isAgent = () => {
    return hasRole('agent');
  };

  const canApproveTrips = () => {
    return hasAnyRole(['manager', 'travel_ops', 'tenant_admin', 'super_admin']);
  };

  const canManageBookings = () => {
    return hasAnyRole(['agent', 'travel_ops', 'tenant_admin', 'super_admin']);
  };

  const refreshProfile = () => {
    if (user?.id) {
      profileOperations?.load(user?.id);
    }
  };

  const value = {
    // State
    user,
    userProfile,
    tenant,
    loading,
    profileLoading,
    
    // Auth methods
    signIn,
    signUp,
    signInWithSSO,
    signOut,
    resetPassword,
    
    // Utility methods
    hasRole,
    hasAnyRole,
    isTenantAdmin,
    isAgent,
    canApproveTrips,
    canManageBookings,
    refreshProfile,
    
    // Computed properties
    isAuthenticated: !!user,
    isProfileLoaded: !!userProfile,
    tenantId: tenant?.id,
    tenantName: tenant?.name,
    userRole: userProfile?.role,
    userName: userProfile?.full_name,
    userEmail: userProfile?.email
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};